import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import { useAuth } from './AuthContext';
import { handleAuthExpired } from './authExpired';

const API_URL = process.env.REACT_APP_API_URL ?? '';

// 🔐 Ikki bosqichli kirish (TOTP) — sozlash va boshqarish (2026-09-27, docs/knowledge/148).
// Majburiy hisob (totp_required) bu sahifaga avtomatik yo'naltiriladi (server 403
// MFA_SETUP_REQUIRED); qolganlar 👤 menyusidan ochadi.
export default function TwoFactorSetup() {
  const { token, user, login, logout } = useAuth();
  const navigate = useNavigate();
  const [status, setStatus] = useState(null);      // {enabled, required, recoveryLeft}
  const [setup, setSetup] = useState(null);        // {secret, qrSvg}
  const [code, setCode] = useState('');
  const [busy, setBusy] = useState(false);
  const [codes, setCodes] = useState(null);        // yangi zaxira kodlar (bir marta ko'rsatiladi)
  const [pendingAuth, setPendingAuth] = useState(null); // enable'dan keyingi yangi token
  const [disableForm, setDisableForm] = useState(null); // {password, code}

  const call = async (path, body) => {
    const res = await fetch(`${API_URL}${path}`, {
      method: body === undefined ? 'GET' : 'POST',
      headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
      body: body === undefined ? undefined : JSON.stringify(body),
    });
    if (res.status === 401) { handleAuthExpired(); throw new Error('unauthorized'); }
    const d = await res.json().catch(() => ({}));
    if (!res.ok) throw new Error(d.error || 'Xato');
    return d;
  };

  useEffect(() => {
    call('/auth/2fa/status').then(setStatus).catch(e => toast.error(e.message));
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const start = async () => {
    setBusy(true);
    try { setSetup(await call('/auth/2fa/setup', {})); setCode(''); }
    catch (e) { toast.error(e.message); }
    finally { setBusy(false); }
  };
  useEffect(() => { if (status && !status.enabled && !setup) start(); }, [status]); // eslint-disable-line react-hooks/exhaustive-deps

  const enable = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const d = await call('/auth/2fa/enable', { code });
      setCodes(d.recoveryCodes);
      setPendingAuth({ token: d.token, user: d.user });
      setStatus({ enabled: true, required: status && status.required, recoveryLeft: d.recoveryCodes.length });
      setSetup(null);
      toast.success('✅ Ikki bosqichli kirish yoqildi');
    } catch (e2) { toast.error(e2.message); }
    finally { setBusy(false); }
  };

  const regenerate = async (e) => {
    e.preventDefault();
    setBusy(true);
    try { const d = await call('/auth/2fa/recovery', { code }); setCodes(d.recoveryCodes); setCode(''); setStatus(s => ({ ...s, recoveryLeft: d.recoveryCodes.length })); }
    catch (e2) { toast.error(e2.message); }
    finally { setBusy(false); }
  };

  const disable = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const d = await call('/auth/2fa/disable', disableForm);
      login(d.token, d.user);
      setStatus({ enabled: false, required: false, recoveryLeft: 0 });
      setDisableForm(null);
      toast.success("2FA o'chirildi");
    } catch (e2) { toast.error(e2.message); }
    finally { setBusy(false); }
  };

  const finish = () => {
    if (pendingAuth) login(pendingAuth.token, pendingAuth.user);
    navigate('/');
  };

  const codesText = codes ? `Khorezmai — zaxira kodlar (${user && user.username})\nHar biri BIR MARTA ishlaydi.\n\n${codes.join('\n')}\n` : '';
  const copyCodes = async () => { try { await navigator.clipboard.writeText(codesText); toast.success('Nusxa olindi'); } catch (e) { toast.error("Nusxalab bo'lmadi"); } };
  const downloadCodes = () => {
    const a = document.createElement('a');
    a.href = URL.createObjectURL(new Blob([codesText], { type: 'text/plain' }));
    a.download = `khorezmai-zaxira-kodlar-${(user && user.username) || ''}.txt`;
    a.click(); setTimeout(() => URL.revokeObjectURL(a.href), 2000);
  };

  const card = 'w-full max-w-md p-6 space-y-5 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg shadow-sm sm:p-8';
  const inputCls = 'bg-gray-50 dark:bg-gray-700 border border-gray-300 dark:border-gray-600 text-gray-900 dark:text-white rounded-lg focus:ring-primary-500 focus:border-primary-500 focus:ring-2 focus:outline-none block w-full p-2.5';
  const btn = 'w-full px-5 py-2.5 text-sm font-medium text-center text-white rounded-lg bg-primary-700 dark:bg-primary-600 hover:bg-primary-800 dark:hover:bg-primary-700 disabled:opacity-60 transition';
  const btn2 = 'px-4 py-2 text-sm font-medium rounded-lg border border-gray-200 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-900 dark:text-white hover:bg-gray-100 dark:hover:bg-gray-700 transition';
  const codeInput = (
    <input value={code} onChange={e => setCode(e.target.value)} className={`${inputCls} text-center text-xl tracking-[0.4em] font-mono`}
      inputMode="numeric" autoComplete="one-time-code" placeholder="123456" maxLength={6} required autoFocus />
  );

  return (
    <div className="flex flex-col items-center justify-center min-h-screen px-4 py-8 bg-gray-50 dark:bg-gray-900">
      <div className={card}>
        <div>
          <h2 className="text-xl font-bold text-gray-900 dark:text-white">🔐 Ikki bosqichli kirish</h2>
          <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
            Parol o'g'irlansa ham hisobingizga kirib bo'lmaydi: har kirishda telefondagi ilova ko'rsatgan 6 xonali kod so'raladi.
          </p>
        </div>

        {!status && <div className="text-sm text-gray-400">⏳ Yuklanmoqda…</div>}

        {codes && (
          <div className="space-y-3">
            <div className="p-3 text-sm rounded-lg bg-amber-50 dark:bg-amber-900/30 text-amber-900 dark:text-amber-200 border border-amber-300 dark:border-amber-700">
              <b>Zaxira kodlarni hozir saqlab qo'ying</b> — ular boshqa ko'rsatilmaydi. Telefon yo'qolsa, shu kodlardan biri bilan kirasiz (har biri bir marta).
            </div>
            <div className="grid grid-cols-2 gap-2 p-3 font-mono text-sm rounded-lg bg-gray-50 dark:bg-gray-900 text-gray-900 dark:text-gray-100" translate="no">
              {codes.map(c => <span key={c}>{c}</span>)}
            </div>
            <div className="flex gap-2">
              <button type="button" onClick={copyCodes} className={btn2}>📋 Nusxa olish</button>
              <button type="button" onClick={downloadCodes} className={btn2}>⬇️ .txt yuklab olish</button>
            </div>
            <button type="button" onClick={finish} className={btn}>Saqladim — davom etish</button>
          </div>
        )}

        {!codes && status && !status.enabled && (
          <form onSubmit={enable} className="space-y-4">
            {status.required && (
              <div className="p-3 text-sm rounded-lg bg-primary-50 dark:bg-primary-900/30 text-primary-900 dark:text-primary-200">
                Bu hisob uchun ikki bosqichli kirish <b>majburiy</b> — sozlamaguningizcha panel ochilmaydi.
              </div>
            )}
            <ol className="space-y-1 text-sm text-gray-700 dark:text-gray-300 list-decimal list-inside">
              <li>Telefonga <b>Google Authenticator</b>, <b>Authy</b> yoki <b>1Password</b> o'rnating.</li>
              <li>Ilovada «+» → «QR kodni skanerlash».</li>
              <li>Ilova ko'rsatgan 6 xonali kodni pastga yozing.</li>
            </ol>
            {setup ? (
              <>
                <div className="mx-auto w-52 h-52 p-2 bg-white rounded-lg border border-gray-200" dangerouslySetInnerHTML={{ __html: setup.qrSvg }} />
                <details className="text-xs text-gray-500 dark:text-gray-400">
                  <summary className="cursor-pointer">QR skanerlanmayaptimi? Kalitni qo'lda kiriting</summary>
                  <div className="mt-2 p-2 font-mono break-all rounded bg-gray-50 dark:bg-gray-900 text-gray-900 dark:text-gray-100 select-all" translate="no">{setup.secret}</div>
                </details>
                {codeInput}
                <button type="submit" disabled={busy || code.length !== 6} className={btn}>{busy ? 'Tekshirilmoqda…' : 'Tasdiqlash va yoqish'}</button>
              </>
            ) : <div className="text-sm text-gray-400">⏳ QR kod tayyorlanmoqda…</div>}
          </form>
        )}

        {!codes && status && status.enabled && (
          <div className="space-y-4">
            <div className="p-3 text-sm rounded-lg bg-green-50 dark:bg-green-900/30 text-green-800 dark:text-green-300">
              ✅ Yoqilgan{status.required ? ' (majburiy)' : ''}. Qolgan zaxira kodlar: <b>{status.recoveryLeft}</b>
            </div>
            <form onSubmit={regenerate} className="space-y-2">
              <div className="text-sm font-medium text-gray-900 dark:text-white">Yangi zaxira kodlar (eskilari bekor bo'ladi)</div>
              {codeInput}
              <button type="submit" disabled={busy || code.length !== 6} className={btn}>Zaxira kodlarni yangilash</button>
            </form>
            {!status.required && (disableForm ? (
              <form onSubmit={disable} className="space-y-2 pt-2 border-t border-gray-200 dark:border-gray-700">
                <input type="password" placeholder="Parol" value={disableForm.password} onChange={e => setDisableForm(f => ({ ...f, password: e.target.value }))} className={inputCls} required />
                <input placeholder="6 xonali kod" value={disableForm.code} onChange={e => setDisableForm(f => ({ ...f, code: e.target.value }))} className={inputCls} inputMode="numeric" maxLength={6} required />
                <button type="submit" disabled={busy} className="w-full px-5 py-2.5 text-sm font-medium text-white rounded-lg bg-red-600 hover:bg-red-700 disabled:opacity-60">2FA ni o'chirish</button>
              </form>
            ) : (
              <button type="button" onClick={() => setDisableForm({ password: '', code: '' })} className="text-sm text-red-600 dark:text-red-400 hover:underline">2FA ni o'chirish…</button>
            ))}
          </div>
        )}

        {!codes && <div className="flex justify-between pt-2 text-sm">
          {status && status.required && !status.enabled
            ? <button type="button" onClick={() => { logout(); navigate('/login'); }} className="text-gray-500 hover:underline">Chiqish</button>
            : <button type="button" onClick={() => navigate('/')} className="text-gray-500 hover:underline">← Panelga qaytish</button>}
        </div>}
      </div>
    </div>
  );
}
